import React, { useState, useRef } from "react";
import {
  Box,
  Button,
  Grid,
  TextField,
  Typography,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Dialog,
  DialogContent,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

import ProgressBar from "../../components/progressbar/signup-progressbar";

import "../../styles/header.css";
import BASE_URL from "../../components/config/appconfig";
import SnackbarAlert from "../../components/snackbarAlert/snackbarAlert";
import { useNavigate } from "react-router-dom";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import HeadsetMicOutlinedIcon from "@mui/icons-material/HeadsetMicOutlined";
import logo from "../../assets/images/main_logo.png";
import usePageMeta from "../../utils/usePageMeta";

const CTCAP_EMAIL = "churchhealth@citytocityasiapacific.com";

const frameworkSections = [
  {
    label: "SECTION ONE: WORSHIP",
    items: [
      "Scripture as Authority and Centrality of the Gospel",
      "Worship, Preaching, Sacraments/Ordinances",
      "Primacy of Prayer",
    ],
  },
  {
    label: "SECTION TWO: DISCIPLESHIP & LEADERSHIP DEVELOPMENT",
    items: [
      "Discipleship",
      "New Testament Patterns of Church Life",
      "Leadership Development and Biblically Qualified Elders",
      "Culture of Generosity and Good Stewardship & Governance",
    ],
  },
  {
    label: "SECTION THREE: MISSION",
    items: [
      "Cultural Renewal & Evangelism as a Church",
      "Believers Able to Share the Gospel in Multiple Contextual Ways",
      "Church Planting, Partnerships & Movement",
    ],
  },
];

const gospelMovements = [
  {
    num: "01",
    label: "Worship",
    desc: "where hearts are re-centered on God's glory and grace.",
  },
  {
    num: "02",
    label: "Discipleship & Leadership Development",
    desc: "where lives are reshaped by the Gospel's logic of grace over performance.",
  },
  {
    num: "03",
    label: "Mission",
    desc: "where the church turns outward in love toward the city, engaging culture, work, and also with brokenness in Gospel hope.",
  },
];

export default function SignUpForm() {
  usePageMeta(
    "Sign Up",
    "Create your account for the Gospel and Church Health Framework.",
  );
  const navigate = useNavigate();
  const signupRef = useRef(null);

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [step, setStep] = useState(1);
  const [participated, setParticipated] = useState(null);
  const [connected, setConnected] = useState("");
  const [contact, setContact] = useState("");
  const [agreeTerms, setAgreeTerms] = useState(false);
  const [termsOpen, setTermsOpen] = useState(false);
  const [termsTab, setTermsTab] = useState("terms");
  const [formData, setFormData] = React.useState({
    fullName: "",
    city: "",
    churchAge: "",
    email: "",
    country: "",
    churchName: "",
  });
  const [errors, setErrors] = useState({
    fullName: "",
    city: "",
    churchAge: "",
    email: "",
    country: "",
    churchName: "",
  });

  const validateEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  const validateField = (field, value) => {
    let error = "";
    const trimmedValue = String(value).trim();
    if (!trimmedValue) error = "This field is required";
    if (field === "email" && trimmedValue && !validateEmail(trimmedValue))
      error = "Enter a valid email address";
    setErrors((prev) => ({ ...prev, [field]: error }));
    return !error;
  };

  const validateStepOne = () => {
    let valid = true;
    Object.keys(formData).forEach((field) => {
      if (!validateField(field, formData[field])) valid = false;
    });
    return valid;
  };

  const showSnackbar = (message, severity = "success") =>
    setSnackbar({ open: true, message, severity });

  const hideSnackbar = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  const handleChange = (field) => (event) => {
    const value = event.target.value;
    setFormData((prev) => ({ ...prev, [field]: value }));
    validateField(field, value);
  };

  const churchAgeOptions = [...Array(51).keys(), "50+"];

  const initialFormData = {
    fullName: "",
    city: "",
    churchAge: "",
    email: "",
    country: "",
    churchName: "",
  };

  const handleCancel = () => {
    setStep(1);
    setParticipated(null);
    setConnected("");
    setContact("");
    setFormData(initialFormData);
    setErrors({
      fullName: "",
      city: "",
      churchAge: "",
      email: "",
      country: "",
      churchName: "",
    });
  };

  const handleSubmit = async () => {
    try {
      const payload = {
        ...formData,
        participated,
        connected,
        contact,
      };

      console.log("Sending payload:", payload);

      const response = await fetch(`${BASE_URL}/api/people`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      const result = await response.json();

      console.log("Status:", response.status);
      console.log("Response:", result);

      if (response.ok && result.success) {
        showSnackbar(result.message, "success");
        setTimeout(() => navigate("/sign-in"), 2000);
      } else {
        showSnackbar(result.message || "Request failed", "error");
      }
    } catch (err) {
      console.error(err);
      showSnackbar("Something went wrong", "error");
    }
  };
  // const scrollToSignup = () =>
  //   signupRef.current?.scrollIntoView({ behavior: "smooth" });

  let itemCounter = 1;

  return (
    <>
      {/* ── HEADER ── */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          px: { xs: 3, md: 5 },
          py: 2,
          bgcolor: "#fff",
          borderBottom: "1px solid #f0f0f0",
        }}
      >
        <img
          src={logo}
          alt="City to City Asia Pacific"
          style={{ height: "auto", width: "150px" }}
        />
        <Button
          variant="outlined"
          startIcon={<HeadsetMicOutlinedIcon sx={{ fontSize: 13 }} />}
          onClick={() => window.open(`mailto:${CTCAP_EMAIL}`, "_blank")}
          sx={{
            bgcolor: "transparent",
            border: "1px solid #d0d0d0",
            color: "#666",
            "&:hover": { bgcolor: "transparent", borderColor: "#999" },
            fontSize: 11,
            fontWeight: 600,
            borderRadius: 2,
            px: 2,
            py: 1,
            boxShadow: "none",
            textTransform: "uppercase",
            letterSpacing: 0.8,
          }}
        >
          Contact Us
        </Button>
      </Box>

      {/* ── SECTION 1: HERO ── */}
      {step === 1 && (
        <Box
          sx={{
            bgcolor: "#fff",
            pt: { xs: 10, md: 16 },
            pb: { xs: 10, md: 14 },
            px: { xs: 4, md: 6 },
            textAlign: "center",
          }}
        >
          {/* Tag */}

          {/* Main title */}
          <Typography
            sx={{
              fontFamily: "'Gotham Black', sans-serif",
              fontWeight: 900,
              fontSize: { xs: "38px", sm: "54px", md: "72px" },
              lineHeight: 1.05,
              color: "#0d1c26",
              maxWidth: "900px",
              mb: 2,
              mx: "auto",
              letterSpacing: "-1px",
            }}
          >
            The Gospel and <b></b>
            Church Health Framework
          </Typography>
          <Typography
            sx={{
              color: "#009ccc",
              fontSize: "14px",
              fontWeight: 700,

              letterSpacing: "3px",
              mb: 6,
            }}
          >
            A reflection and discussion tool for pastors and leaders
          </Typography>

          {/* Description */}
          <Typography
            sx={{
              fontSize: { xs: "22px", md: "24px" },
              color: "#555",
              lineHeight: 1.8,
              maxWidth: "1200px",
              mx: "auto",
              mb: 1.5,
            }}
          >
            The Gospel is not merely the doorway into faith. It is also the
            power for everything we do as believers, leaders, pastors and as a
            church.
          </Typography>
          <Typography
            sx={{
              fontSize: { xs: "16px", md: "18px" },
              color: "#555",
              lineHeight: 1.8,
              maxWidth: "1000px",
              mx: "auto",
              mb: 6,
            }}
          >
            This Gospel and Church Health Framework is built on the conviction
            that when the Gospel is truly front and centre, the local church
            flourishes — worship is renewed, disciples are formed, leaders are
            empowered, and integrated mission becomes a joyful overflow of love.
          </Typography>
        </Box>
      )}

      {/* ── SECTION 2: DARK — MIRROR OF GRACE ── */}
      {step === 1 && (
        <Box
          sx={{
            bgcolor: "#263842",
            py: { xs: 9, md: 14 },
            px: { xs: 4, md: 8 },
          }}
        >
          <Box sx={{ maxWidth: "1200px", mx: "auto" }}>
            {/* Large italic statement */}
            <Typography
              sx={{
                fontStyle: "italic",
                fontWeight: 800,
                fontSize: { xs: "15px", sm: "20px", md: "30px" },
                color: "#fff",
                lineHeight: 1.2,
                mb: 6,
                letterSpacing: "-0.5px",
              }}
            >
              This framework is not an assessment or a scorecard. It is a mirror
              of grace — a way of asking: "Is the Gospel central and beautiful
              among us in our church?
            </Typography>

            <Box sx={{ width: 40, height: 1, bgcolor: "#444", mb: 5 }} />

            {/* Supporting text */}
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 4,
                maxWidth: "860px",
              }}
            >
              <Typography
                sx={{
                  fontSize: { xs: "14px", md: "15px" },
                  color: "#fff",
                  lineHeight: 1.9,
                }}
              >
                Inwardly, this framework calls for a renewed awe — a rediscovery
                of Gospel joy in Scripture, worship, and prayer.
              </Typography>

              <Typography
                sx={{
                  fontSize: { xs: "14px", md: "15px" },
                  color: "#fff",
                  lineHeight: 1.9,
                }}
              >
                Outwardly, it calls for a renewed engagement with the city — a
                passion to see the good news of Jesus heal what is broken in
                culture, relationships, and institutions.
              </Typography>

              <Typography
                sx={{
                  fontSize: { xs: "14px", md: "15px" },
                  color: "#fff",
                  lineHeight: 1.9,
                }}
              >
                When both movements — inward renewal and outward mission — flow
                together, the church becomes a{" "}
                <span
                  style={{
                    color: "rgba(255,255,255,0.75)",
                    fontWeight: 500,
                  }}
                >
                  living, multiplying community of grace
                </span>{" "}
                that loves its city to life.
              </Typography>
            </Box>
          </Box>
        </Box>
      )}

      {/* ── SECTION 3: MOVEMENT CARDS ── */}
      {step === 1 && (
        <Box
          sx={{
            py: { xs: 9, md: 13 },
            px: { xs: 4, md: 8 },
          }}
        >
          <Box sx={{ maxWidth: "1200px", mx: "auto" }}>
            {/* Intro line */}
            <Typography
              sx={{
                fontSize: { xs: "15px", md: "17px" },
                color: "#555",
                lineHeight: 1.75,
                mb: 5,
              }}
            >
              In this framework, pastors and leaders will journey through three
              Gospel movements:
            </Typography>

            {/* Cards */}
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", md: "repeat(3, 1fr)" },
                gap: 2.5,
                mb: 7,
              }}
            >
              {gospelMovements.map((m) => (
                <Box
                  key={m.num}
                  sx={{
                    bgcolor: "#fff",
                    borderRadius: "16px",
                    border: "2px solid #9AA4AF",
                    p: 3.5,
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "space-between",
                  }}
                >
                  {/* Top: number */}
                  <Typography
                    sx={{
                      fontSize: "32px",
                      fontWeight: 700,
                      color: "#2a2a2a",
                      lineHeight: 1,
                      fontVariantNumeric: "tabular-nums",
                      mb: 5,
                    }}
                  >
                    {m.num}
                  </Typography>

                  {/* Bottom: title + desc */}
                  <Box>
                    <Typography
                      sx={{
                        fontSize: { xs: "22px", md: "24px" },
                        fontWeight: 700,
                        color: "#1a1a1a",
                        lineHeight: 1.25,
                        mb: 1,
                      }}
                    >
                      {m.label}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: "13px",
                        color: "#999",
                        lineHeight: 1.65,
                      }}
                    >
                      {m.desc}
                    </Typography>
                  </Box>
                </Box>
              ))}
            </Box>

            {/* Encouragement — minimal, highlighted */}
            <Box
              sx={{
                display: "flex",
                alignItems: "flex-start",
                gap: 2.5,
              }}
            >
              <Box
                sx={{
                  width: "3px",
                  flexShrink: 0,
                  alignSelf: "stretch",
                  bgcolor: "#ed1c2e",
                  borderRadius: "2px",
                }}
              />
              <Typography
                sx={{
                  fontSize: { xs: "14px", md: "15px" },
                  color: "#444",
                  lineHeight: 1.85,
                }}
              >
                We encourage pastors and leaders to walk through the Gospel and
                Church Health Framework{" "}
                <strong style={{ color: "#0d1c26" }}>every six months</strong> —
                guiding them through{" "}
                <strong style={{ color: "#0d1c26" }}>
                  30 questions for reflection and discussion
                </strong>{" "}
                across 10 sections.
              </Typography>
            </Box>
          </Box>
        </Box>
      )}

      {/* ── SECTION 4: FRAMEWORK + SIGNUP / STEPS 2-6 ── */}
      <Box
        ref={signupRef}
        sx={{
          px: { xs: 3, md: 6 },
          pt: { xs: 6, md: 8 },
          pb: { xs: 6, md: 10 },
        }}
      >
        {step === 1 ? (
          <Box
            sx={{
              maxWidth: "1200px",
              mx: "auto",
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
              gap: { xs: 5, md: 8 },
              alignItems: "start",
            }}
          >
            {/* LEFT: Framework sections */}
            <Box>
              <Typography
                sx={{
                  fontFamily: "'Gotham Black', sans-serif",
                  fontWeight: 800,
                  fontSize: { xs: "20px", sm: "24px", md: "28px" },
                  color: "#0d1c26",
                  lineHeight: 1.2,
                  mb: 5,
                }}
              >
                The Gospel and Church Health Framework
              </Typography>

              {frameworkSections.map((section) => (
                <Box key={section.label} mb={4}>
                  <Typography
                    sx={{
                      fontSize: "14px",
                      fontWeight: 700,
                      color: "#009ccc",
                      textTransform: "uppercase",
                      letterSpacing: "1px",
                      mb: 1.5,
                    }}
                  >
                    {section.label}
                  </Typography>
                  {section.items.map((item) => {
                    const num = itemCounter++;
                    return (
                      <Box
                        key={item}
                        sx={{
                          display: "flex",
                          gap: 1.5,
                          mb: 0.75,
                          alignItems: "flex-start",
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize: "16px",
                            color: "#888",
                            lineHeight: 1.7,
                            minWidth: "24px",
                            fontWeight: 500,
                          }}
                        >
                          {num})
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: "16px",
                            color: "#444",
                            lineHeight: 1.7,
                          }}
                        >
                          {item}
                        </Typography>
                      </Box>
                    );
                  })}
                </Box>
              ))}
            </Box>

            {/* RIGHT: Signup card (sticky) */}
            <Box
              sx={{
                bgcolor: "#fff",
                borderRadius: 4,
                border: "1px solid #e8e8e8",
                p: { xs: 3.5, md: 5 },
                position: "sticky",
                top: 24,
                alignSelf: "start",
                "& .MuiInputBase-input": { fontSize: "14px" },
              }}
            >
              <Typography
                variant="h5"
                sx={{
                  fontWeight: 700,
                  fontSize: { xs: "20px", sm: "22px" },
                  color: "#0d1c26",
                  mb: 3,
                }}
              >
                Get Started
              </Typography>
              <Grid container spacing={2.5}>
                {/* Row 1 */}
                <Grid item size={{ xs: 12, md: 6 }}>
                  <Typography className="editor-dialog-label">
                    Full Name <span className="required-star">*</span>
                  </Typography>
                  <TextField
                    required
                    name="fullName"
                    fullWidth
                    size="small"
                    value={formData.fullName}
                    placeholder="Enter your full name here"
                    onChange={handleChange("fullName")}
                    error={Boolean(errors.fullName)}
                    helperText={errors.fullName}
                  />
                </Grid>

                <Grid item size={{ xs: 12, md: 6 }}>
                  <Typography className="editor-dialog-label">
                    Email Address <span className="required-star">*</span>
                  </Typography>
                  <TextField
                    name="email"
                    fullWidth
                    size="small"
                    value={formData.email}
                    placeholder="Enter your email here"
                    onChange={handleChange("email")}
                    error={Boolean(errors.email)}
                    helperText={errors.email}
                  />
                </Grid>

                {/* Row 2 */}
                <Grid item size={{ xs: 12, md: 6 }}>
                  <Typography className="editor-dialog-label">
                    City <span className="required-star">*</span>
                  </Typography>
                  <TextField
                    name="city"
                    fullWidth
                    size="small"
                    value={formData.city}
                    placeholder="Enter your city here"
                    onChange={handleChange("city")}
                    error={Boolean(errors.city)}
                    helperText={errors.city}
                  />
                </Grid>

                <Grid item size={{ xs: 12, md: 6 }}>
                  <Typography className="editor-dialog-label">
                    Country <span className="required-star">*</span>
                  </Typography>
                  <TextField
                    name="country"
                    fullWidth
                    size="small"
                    placeholder="Enter your country here"
                    value={formData.country}
                    onChange={handleChange("country")}
                    error={Boolean(errors.country)}
                    helperText={errors.country}
                  />
                </Grid>

                {/* Row 3 */}
                <Grid item size={{ xs: 12, md: 6 }}>
                  <Typography className="editor-dialog-label">
                    Age of the Church <span className="required-star">*</span>
                  </Typography>
                  <TextField
                    select
                    name="churchAge"
                    fullWidth
                    size="small"
                    value={formData.churchAge}
                    sx={{
                      "& .MuiSelect-select": {
                        color: formData.churchAge ? "#000" : "#9e9e9e",
                      },
                    }}
                    onChange={handleChange("churchAge")}
                    error={Boolean(errors.churchAge)}
                    helperText={errors.churchAge}
                    slotProps={{
                      select: {
                        displayEmpty: true,
                        MenuProps: {
                          PaperProps: { style: { maxHeight: 200 } },
                        },
                      },
                    }}
                  >
                    <MenuItem value="">Choose church age</MenuItem>
                    {churchAgeOptions.map((age) => (
                      <MenuItem key={age} value={age}>
                        {age}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>

                <Grid item size={{ xs: 12, md: 6 }}>
                  <Typography className="editor-dialog-label">
                    Name of the Church <span className="required-star">*</span>
                  </Typography>
                  <TextField
                    placeholder="Enter your church name here"
                    name="churchName"
                    fullWidth
                    size="small"
                    value={formData.churchName}
                    onChange={handleChange("churchName")}
                    error={Boolean(errors.churchName)}
                    helperText={errors.churchName}
                  />
                </Grid>
              </Grid>

              {/* Terms Checkbox */}
              <Box mt={2} display="flex" alignItems="center">
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={agreeTerms}
                      onChange={(e) => setAgreeTerms(e.target.checked)}
                      sx={{
                        color: "#313131",
                        "&.Mui-checked": { color: "#313131" },
                      }}
                    />
                  }
                  label={
                    <Typography fontSize={{ xs: 12, sm: 14 }}>
                      I agree to all the{" "}
                      <span
                        style={{
                          cursor: "pointer",
                          textDecoration: "underline",
                        }}
                        onClick={(e) => {
                          e.preventDefault();
                          setTermsTab("terms");
                          setTermsOpen(true);
                        }}
                      >
                        Terms
                      </span>{" "}
                      and{" "}
                      <span
                        style={{
                          cursor: "pointer",
                          textDecoration: "underline",
                        }}
                        onClick={(e) => {
                          e.preventDefault();
                          setTermsTab("privacy");
                          setTermsOpen(true);
                        }}
                      >
                        Privacy Policies
                      </span>
                    </Typography>
                  }
                />
              </Box>
              <Box sx={{ mt: 2 }}>
                <Button
                  variant="contained"
                  onClick={() => {
                    const fieldsValid = validateStepOne();
                    if (!fieldsValid) return;
                    if (!agreeTerms) {
                      showSnackbar(
                        "Please agree to the Terms and Privacy Policies to continue.",
                        "error",
                      );
                      return;
                    }
                    setStep(2);
                  }}
                  sx={{
                    width: "100%",
                    height: 48,
                    bgcolor: "#ed1c2e",
                    textTransform: "none",
                    fontWeight: 600,
                    fontSize: "15px",
                    borderRadius: "8px",
                    boxShadow: "none",
                    "&:hover": { bgcolor: "#ed1c2e", boxShadow: "none" },
                  }}
                >
                  Sign Up
                </Button>
              </Box>
              <Box sx={{ textAlign: "center", mt: 2 }}>
                <Typography fontSize={{ xs: 13, sm: 14 }}>
                  <span style={{ color: "#888" }}>
                    Already have an account?{" "}
                  </span>
                  <span
                    onClick={() => navigate("/sign-in")}
                    style={{
                      color: "#0d1c26",
                      cursor: "pointer",
                      fontWeight: 600,
                    }}
                  >
                    Sign In
                  </span>
                </Typography>
              </Box>
            </Box>
          </Box>
        ) : (
          /* Steps 2-6: centered single column */
          <Box sx={{ width: "100%" }}>
            <Box sx={{ mb: { xs: 3, sm: 5 }, maxWidth: "1200px", mx: "auto" }}>
              <ProgressBar
                step={step}
                participated={participated}
                connected={connected}
                onCancel={handleCancel}
              />
            </Box>

            {/* STEP 2 */}
            {step === 2 && (
              <>
                <Box sx={{ maxWidth: "1200px", mx: "auto" }}>
                  <Box sx={{ mt: { xs: 4, sm: 10 } }}>
                    <Typography
                      variant="h5"
                      mb={{ xs: 3, sm: 5 }}
                      sx={{ fontSize: { xs: "16px", sm: "20px", md: "24px" } }}
                    >
                      Have you participated in any CTC Asia Pacific Training?
                    </Typography>
                    <Box display="flex" flexDirection="column" gap={3}>
                      <Box display="flex" gap={3}>
                        <Button
                          variant={
                            participated === "yes" ? "contained" : "outlined"
                          }
                          onClick={() => setParticipated("yes")}
                          sx={{
                            width: 77,
                            height: 36,
                            borderRadius: "10px",
                            borderWidth: "1px",
                            borderStyle: "solid",
                            borderColor: "#ED1C2E",
                            padding: "8px 24px",
                            color: "#ED1C2E",
                            boxShadow: "none",
                            textTransform: "none",
                            "&.MuiButton-contained": {
                              color: "#fff",
                              backgroundColor: "#ED1C2E",
                              boxShadow: "none",
                            },
                          }}
                        >
                          Yes
                        </Button>
                        <Button
                          variant={
                            participated === "no" ? "contained" : "outlined"
                          }
                          onClick={() => setParticipated("no")}
                          sx={{
                            width: 77,
                            height: 36,
                            borderRadius: "10px",
                            borderWidth: "1px",
                            borderStyle: "solid",
                            borderColor: "#263842",
                            padding: "8px 24px",
                            color: "#263842",
                            textTransform: "none",
                            "&.MuiButton-contained": {
                              backgroundColor: "#263842",
                              color: "#fff",
                            },
                          }}
                        >
                          No
                        </Button>
                      </Box>
                      <Box display="flex" justifyContent="flex-end" gap={2}>
                        <Button
                          variant="outlined"
                          onClick={() => setStep(1)}
                          className="previous-button-signup"
                        >
                          Previous
                        </Button>
                        <Button
                          variant="contained"
                          disabled={!participated}
                          onClick={() => setStep(3)}
                          className={`next-btn-signup ${
                            participated ? "enabled" : "disabled"
                          }`}
                        >
                          NEXT{" "}
                          <ArrowForwardIcon sx={{ ml: 1, fontSize: "18px" }} />
                        </Button>
                      </Box>
                    </Box>
                  </Box>
                </Box>
              </>
            )}

            {/* STEP 3 */}
            {step === 3 && (
              <>
                <Box sx={{ maxWidth: "1200px", mx: "auto" }}>
                  <Box sx={{ mt: { xs: 4, sm: 10 } }}>
                    <Typography
                      variant="h5"
                      mb={{ xs: 3, sm: 5 }}
                      sx={{ fontSize: { xs: "16px", sm: "20px", md: "24px" } }}
                    >
                      Are you connected with any CTC Leaders in your
                      City/Country/Asia?
                    </Typography>
                    <Box display="flex" flexDirection="column" gap={3}>
                      <Box display="flex" gap={3}>
                        <Button
                          variant={
                            connected === "yes" ? "contained" : "outlined"
                          }
                          onClick={() => setConnected("yes")}
                          sx={{
                            width: 77,
                            height: 36,
                            borderRadius: "10px",
                            border: "1px solid #ED1C2E",
                            padding: "8px 24px",
                            color: "#ED1C2E",
                            textTransform: "none",
                            boxShadow: "none",
                            "&.MuiButton-contained": {
                              color: "#fff",
                              backgroundColor: "#ED1C2E",
                            },
                          }}
                        >
                          Yes
                        </Button>
                        <Button
                          variant={
                            connected === "no" ? "contained" : "outlined"
                          }
                          onClick={() => setConnected("no")}
                          sx={{
                            width: 77,
                            height: 36,
                            borderRadius: "10px",
                            border: "1px solid #263842",
                            padding: "8px 24px",
                            color: "#263842",
                            textTransform: "none",
                            "&.MuiButton-contained": {
                              backgroundColor: "#263842",
                              color: "#fff",
                            },
                          }}
                        >
                          No
                        </Button>
                      </Box>
                      <Box display="flex" justifyContent="flex-end" gap={2}>
                        <Button
                          variant="outlined"
                          onClick={() => setStep(2)}
                          className="previous-button-signup"
                        >
                          Previous
                        </Button>
                        <Button
                          variant="contained"
                          disabled={!connected}
                          onClick={() => {
                            if (participated === "no" && connected === "no") {
                              setStep(4);
                            } else {
                              setStep(6);
                            }
                          }}
                          className={`next-btn-signup ${
                            participated === "no" && connected === "no"
                              ? "btn-signup-94"
                              : "btn-signup-110"
                          } ${connected ? "enabled" : "disabled"}`}
                        >
                          {participated === "no" && connected === "no"
                            ? "NEXT "
                            : "Submit "}{" "}
                          <ArrowForwardIcon sx={{ ml: 1, fontSize: "18px" }} />
                        </Button>
                      </Box>
                    </Box>
                  </Box>
                </Box>
              </>
            )}

            {/* STEP 4 */}
            {step === 4 && (
              <Box sx={{ maxWidth: "1200px", mx: "auto" }}>
                <Box sx={{ mt: { xs: 4, sm: 10 } }}>
                  <Typography
                    variant="h5"
                    mb={{ xs: 3, sm: 5 }}
                    sx={{ fontSize: { xs: "16px", sm: "20px", md: "24px" } }}
                  >
                    Would you like us to contact you for training opportunities
                    from CTCAP?
                  </Typography>
                  <Box display="flex" flexDirection="column" gap={3}>
                    <Box display="flex" gap={3}>
                      <Button
                        variant={contact === "yes" ? "contained" : "outlined"}
                        onClick={() => setContact("yes")}
                        sx={{
                          width: 77,
                          height: 36,
                          borderRadius: "10px",
                          border: "1px solid #ED1C2E",
                          padding: "8px 24px",
                          color: "#ED1C2E",
                          textTransform: "none",
                          "&.MuiButton-contained": {
                            color: "#fff",
                            backgroundColor: "#ED1C2E",
                          },
                        }}
                      >
                        Yes
                      </Button>
                      <Button
                        variant={contact === "no" ? "contained" : "outlined"}
                        onClick={() => setContact("no")}
                        sx={{
                          width: 77,
                          height: 36,
                          borderRadius: "10px",
                          border: "1px solid #263842",
                          padding: "8px 24px",
                          color: "#263842",
                          textTransform: "none",
                          "&.MuiButton-contained": {
                            backgroundColor: "#263842",
                            color: "#fff",
                          },
                        }}
                      >
                        No
                      </Button>
                    </Box>
                    <Box display="flex" justifyContent="flex-end" gap={2}>
                      <Button
                        variant="outlined"
                        onClick={() => setStep(3)}
                        className="previous-button-signup"
                      >
                        Previous
                      </Button>
                      <Button
                        variant="contained"
                        disabled={!contact}
                        onClick={() => setStep(5)}
                        className={`next-btn-signup btn-signup-110 ${
                          contact ? "enabled" : "disabled"
                        }`}
                      >
                        Submit{" "}
                        <ArrowForwardIcon sx={{ ml: 1, fontSize: "18px" }} />
                      </Button>
                    </Box>
                  </Box>
                </Box>
              </Box>
            )}

            {/* STEP 5 - PREVIEW */}
            {step === 5 && (
              <>
                <Box sx={{ maxWidth: "1200px", mx: "auto" }}>
                  <Grid container spacing={3} mb={3}>
                    <Grid item size={{ xs: 12, md: 6 }}>
                      <Typography variant="body2" color="text.secondary" mb={1}>
                        Full Name
                      </Typography>
                      <Typography sx={{ paddingTop: "12px" }}>
                        {formData.fullName || ""}
                      </Typography>
                    </Grid>
                    <Grid item size={{ xs: 12, md: 6 }}>
                      <Typography variant="body2" color="text.secondary" mb={1}>
                        Email Address
                      </Typography>
                      <Typography sx={{ paddingTop: "12px" }}>
                        {formData.email || ""}
                      </Typography>
                    </Grid>
                  </Grid>
                  <Grid container spacing={3} mb={3}>
                    <Grid item size={{ xs: 12, md: 6 }}>
                      <Typography variant="body2" color="text.secondary" mb={1}>
                        City
                      </Typography>
                      <Typography sx={{ paddingTop: "12px" }}>
                        {formData.city || ""}
                      </Typography>
                    </Grid>
                    <Grid item size={{ xs: 12, md: 6 }}>
                      <Typography variant="body2" color="text.secondary" mb={1}>
                        Country
                      </Typography>
                      <Typography sx={{ paddingTop: "12px" }}>
                        {formData.country || ""}
                      </Typography>
                    </Grid>
                  </Grid>
                  <Grid container spacing={3} mb={4}>
                    <Grid item size={{ xs: 12, md: 6 }}>
                      <Typography variant="body2" color="text.secondary" mb={1}>
                        Age of the Church
                      </Typography>
                      <Typography sx={{ paddingTop: "12px" }}>
                        {formData.churchAge || ""}
                      </Typography>
                    </Grid>
                    <Grid item size={{ xs: 12, md: 6 }}>
                      <Typography variant="body2" color="text.secondary" mb={1}>
                        Name of the Church
                      </Typography>
                      <Typography sx={{ paddingTop: "12px" }}>
                        {formData.churchName || ""}
                      </Typography>
                    </Grid>
                  </Grid>
                  <Grid container spacing={2} mb={2}>
                    <Grid item size={{ xs: 12 }}>
                      <Typography variant="body2" color="text.secondary" mb={1}>
                        Have you participated in any CTC Asia Pacific Training?
                      </Typography>
                      <Box sx={{ p: 1 }}>
                        <Typography fontWeight="medium">
                          {participated === "yes"
                            ? "Yes"
                            : participated === "no"
                              ? "No"
                              : "Not answered"}
                        </Typography>
                      </Box>
                    </Grid>
                  </Grid>
                  <Grid container spacing={2} mb={2}>
                    <Grid item size={{ xs: 12 }}>
                      <Typography variant="body2" color="text.secondary" mb={1}>
                        Are you connected with any CTC Leaders in your
                        City/Country/Asia?
                      </Typography>
                      <Box sx={{ p: 1 }}>
                        <Typography fontWeight="medium">
                          {connected === "yes"
                            ? "Yes"
                            : connected === "no"
                              ? "No"
                              : "Not answered"}
                        </Typography>
                      </Box>
                    </Grid>
                  </Grid>
                  <Grid container spacing={2}>
                    <Grid item size={{ xs: 12 }}>
                      <Typography variant="body2" color="text.secondary" mb={1}>
                        Would you like us to contact you for training
                        opportunities from CTCAP?
                      </Typography>
                      <Box sx={{ p: 1 }}>
                        <Typography fontWeight="medium">
                          {contact === "yes"
                            ? "Yes"
                            : contact === "no"
                              ? "No"
                              : "Not answered"}
                        </Typography>
                      </Box>
                    </Grid>
                  </Grid>
                  <Box display="flex" justifyContent="flex-end" gap={2}>
                    <Button
                      variant="outlined"
                      onClick={() => setStep(4)}
                      className="previous-button-signup"
                    >
                      Previous
                    </Button>
                    <Button
                      variant="contained"
                      disabled={!contact}
                      onClick={() => handleSubmit()}
                      className={`next-btn-signup btn-signup-110 ${
                        contact ? "enabled" : "disabled"
                      }`}
                    >
                      confirm{" "}
                      <ArrowForwardIcon sx={{ ml: 1, fontSize: "18px" }} />
                    </Button>
                  </Box>
                </Box>
              </>
            )}

            {/* STEP 6 - PREVIEW (FROM STEP 3) */}
            {step === 6 && (
              <>
                <Box sx={{ maxWidth: "1200px", mx: "auto" }}>
                  <Grid container spacing={3} mb={3}>
                    <Grid item size={{ xs: 12, md: 6 }}>
                      <Typography variant="body2" color="text.secondary" mb={1}>
                        Full Name
                      </Typography>
                      <Typography sx={{ paddingTop: "12px" }}>
                        {formData.fullName || ""}
                      </Typography>
                    </Grid>
                    <Grid item size={{ xs: 12, md: 6 }}>
                      <Typography variant="body2" color="text.secondary" mb={1}>
                        Email Address
                      </Typography>
                      <Typography sx={{ paddingTop: "12px" }}>
                        {formData.email || ""}
                      </Typography>
                    </Grid>
                  </Grid>
                  <Grid container spacing={3} mb={3}>
                    <Grid item size={{ xs: 12, md: 6 }}>
                      <Typography variant="body2" color="text.secondary" mb={1}>
                        City
                      </Typography>
                      <Typography sx={{ paddingTop: "12px" }}>
                        {formData.city || ""}
                      </Typography>
                    </Grid>
                    <Grid item size={{ xs: 12, md: 6 }}>
                      <Typography variant="body2" color="text.secondary" mb={1}>
                        Country
                      </Typography>
                      <Typography sx={{ paddingTop: "12px" }}>
                        {formData.country || ""}
                      </Typography>
                    </Grid>
                  </Grid>
                  <Grid container spacing={3} mb={4}>
                    <Grid item size={{ xs: 12, md: 6 }}>
                      <Typography variant="body2" color="text.secondary" mb={1}>
                        Age of the Church
                      </Typography>
                      <Typography sx={{ paddingTop: "12px" }}>
                        {formData.churchAge || ""}
                      </Typography>
                    </Grid>
                    <Grid item size={{ xs: 12, md: 6 }}>
                      <Typography variant="body2" color="text.secondary" mb={1}>
                        Name of the Church
                      </Typography>
                      <Typography sx={{ paddingTop: "12px" }}>
                        {formData.churchName || ""}
                      </Typography>
                    </Grid>
                  </Grid>
                  <Grid container spacing={2} mb={2}>
                    <Grid item size={{ xs: 12 }}>
                      <Typography variant="body2" color="text.secondary" mb={1}>
                        Have you participated in any CTC Asia Pacific Training?
                      </Typography>
                      <Typography fontWeight="medium">
                        {participated === "yes"
                          ? "Yes"
                          : participated === "no"
                            ? "No"
                            : "Not answered"}
                      </Typography>
                    </Grid>
                  </Grid>
                  <Grid container spacing={2}>
                    <Grid item size={{ xs: 12 }}>
                      <Typography variant="body2" color="text.secondary" mb={1}>
                        Are you connected with any CTC Leaders in your
                        City/Country/Asia?
                      </Typography>
                      <Typography fontWeight="medium">
                        {connected === "yes"
                          ? "Yes"
                          : connected === "no"
                            ? "No"
                            : "Not answered"}
                      </Typography>
                    </Grid>
                  </Grid>
                  <Box display="flex" justifyContent="flex-end" gap={2}>
                    <Button
                      variant="outlined"
                      onClick={() => setStep(3)}
                      className="previous-button-signup"
                    >
                      Previous
                    </Button>
                    <Button
                      variant="contained"
                      onClick={() => handleSubmit()}
                      className="next-btn-signup btn-signup-110 enabled"
                    >
                      confirm{" "}
                      <ArrowForwardIcon sx={{ ml: 1, fontSize: "18px" }} />
                    </Button>
                  </Box>
                </Box>
              </>
            )}
          </Box>
        )}
      </Box>

      <SnackbarAlert
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.severity}
        onClose={hideSnackbar}
      />

      {/* Terms of Service Dialog */}
      <Dialog
        open={termsOpen && termsTab === "terms"}
        onClose={() => setTermsOpen(false)}
        maxWidth={false}
        slotProps={{
          paper: { sx: { borderRadius: "12px", width: 440, height: 500 } },
        }}
      >
        <DialogContent sx={{ p: 0, display: "flex", flexDirection: "column" }}>
          <Box sx={{ position: "relative", pt: 3, pb: 2, px: 3 }}>
            <Typography
              variant="h6"
              fontWeight={700}
              textAlign="center"
              sx={{ fontSize: "20px", color: "#111" }}
            >
              Terms and Conditions
            </Typography>
            <IconButton
              onClick={() => setTermsOpen(false)}
              size="small"
              sx={{ position: "absolute", top: 8, right: 8, color: "#888" }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
          <Box sx={{ overflowY: "auto", px: 3, pb: 1, flex: 1 }}>
            <Typography variant="body2" lineHeight={1.85} mb={2}>
              By accessing or using the Gospel and Church Health Framework ("the
              Platform") provided by City to City Asia Pacific ("CTCAP"), you
              agree to be bound by these Terms of Service. If you do not agree
              to these terms, please do not use the Platform.
            </Typography>
            <Typography variant="body2" lineHeight={1.85} mb={2}>
              The Platform is intended solely for pastors, church leaders, and
              ministry workers for the purpose of church health reflection and
              Framework. You agree to use the Platform only for its intended
              purpose and not to share your account credentials with others.
              Each registration is personal and non-transferable.
            </Typography>
            <Typography variant="body2" lineHeight={1.85} mb={2}>
              You agree to provide accurate and complete information during
              registration. CTCAP reserves the right to approve or decline
              registrations at its sole discretion. You are responsible for
              maintaining the confidentiality of your account and for all
              activities that occur under your account.
            </Typography>
            <Typography variant="body2" lineHeight={1.85} mb={2}>
              Your Framework responses are confidential and will only be
              accessible to you and authorised CTCAP administrators. Aggregated,
              anonymised data may be used by CTCAP for research and improvement
              of the Framework. CTCAP will not share individual church data with
              third parties without your consent.
            </Typography>
            <Typography variant="body2" lineHeight={1.85} mb={2}>
              All content on the Platform, including but not limited to the
              Framework questions, structure, and materials, is the intellectual
              property of City to City Asia Pacific. You may not reproduce,
              distribute, or create derivative works without express written
              permission from CTCAP.
            </Typography>
            <Typography variant="body2" lineHeight={1.85}>
              CTCAP reserves the right to modify these Terms at any time.
              Continued use of the Platform after changes constitutes acceptance
              of the revised Terms. We will notify registered users of
              significant changes via email.
            </Typography>
          </Box>
          <Box sx={{ p: 3, justifyContent: "center", display: "flex" }}>
            <Button
              onClick={() => setTermsOpen(false)}
              sx={{
                bgcolor: "#ed1c2e",
                color: "#fff",
                fontWeight: 600,
                fontSize: "14px",
                px: 2,
                py: 1,
                borderRadius: "10px",
                textTransform: "none",
                boxShadow: "none",
              }}
            >
              Accept
            </Button>
          </Box>
        </DialogContent>
      </Dialog>

      {/* Privacy Policy Dialog */}
      <Dialog
        open={termsOpen && termsTab === "privacy"}
        onClose={() => setTermsOpen(false)}
        maxWidth={false}
        slotProps={{
          paper: { sx: { borderRadius: "12px", width: 600, height: 550 } },
        }}
      >
        <DialogContent sx={{ p: 0, display: "flex", flexDirection: "column" }}>
          <Box sx={{ position: "relative", pt: 3, pb: 2, px: 3 }}>
            <Typography
              variant="h6"
              fontWeight={700}
              textAlign="center"
              sx={{ fontSize: "20px", color: "#111" }}
            >
              Privacy Policies:
            </Typography>

            <IconButton
              onClick={() => setTermsOpen(false)}
              size="small"
              sx={{ position: "absolute", top: 8, right: 8, color: "#888" }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>

          <Box
            sx={{
              overflowY: "auto",
              px: 4,
              pb: 1,
              flex: 1,
            }}
          >
            <Typography
              variant="body2"
              sx={{
                lineHeight: 1.9,
                color: "#222",
                fontSize: "15px",
                mb: 3,
              }}
            >
              When you register, we collect your full name, email address, city,
              country, church name, and church age. During your use of the
              Platform, we collect your Framework responses, self-ratings, and
              reflection answers.{" "}
              <Box
                component="span"
                sx={{
                  color: "red",
                  fontWeight: 500,
                }}
              >
                We do not share these details with anyone outside City to City.
              </Box>{" "}
              We do not collect payment information.
            </Typography>

            <Typography
              variant="body2"
              sx={{
                lineHeight: 1.9,
                color: "#222",
                fontSize: "15px",
                mb: 3,
              }}
            >
              Your personal information is used to create and manage your
              account, send you Framework-related communications, and allow
              CTCAP administrators to review and approve registrations.
              Framework data is used to generate your personal health report and
              to improve the Framework.{" "}
              <Box
                component="span"
                sx={{
                  color: "red",
                  fontWeight: 500,
                }}
              >
                We do not share this data with anyone.
              </Box>{" "}
              We may contact you about training opportunities if you have opted
              in.
            </Typography>
          </Box>

          <Box sx={{ p: 2, display: "flex", justifyContent: "center" }}>
            <Button
              onClick={() => setTermsOpen(false)}
              sx={{
                bgcolor: "#ed1c2e",
                color: "#fff",
                fontWeight: 600,
                fontSize: "14px",
                height: 48,
                px: 4,
                borderRadius: "10px",
                textTransform: "none",
                boxShadow: "none",
                "&:hover": {
                  bgcolor: "#d41727",
                },
              }}
            >
              Accept
            </Button>
          </Box>
        </DialogContent>
      </Dialog>
    </>
  );
}
